import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as S from "./style";

interface Item {
  label: string;
  component: FC<any>;
  props?: any;
}

interface TabProps {
  items: Item[];
  children?: ReactNode;
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  fitContent?: boolean;
  paramKey?: string;
}

const Tab: FC<TabProps> = ({
  items,
  activeIndex,
  onTabChange,
  fitContent,
  paramKey,
}) => {
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [sliderDimensions, setSliderDimensions] = useState({
    width: 0,
    left: 0,
    shouldAnimate: false,
  });

  const key = paramKey || "tab";
  const tabParam = searchParams.get(key);
  const urlActiveIndex = tabParam
    ? items.findIndex(
        (item) => item.label.toLowerCase() === tabParam.toLowerCase()
      )
    : 0;
  const safeUrlActiveIndex = urlActiveIndex === -1 ? 0 : urlActiveIndex;
  const effectiveActiveIndex =
    typeof activeIndex === "number" ? activeIndex : safeUrlActiveIndex;

  useEffect(() => {
    const activeTabRef = tabRefs.current[effectiveActiveIndex];
    if (activeTabRef) {
      const { offsetWidth: width, offsetLeft: left } = activeTabRef;
      setSliderDimensions((prev) => ({
        width,
        left,
        shouldAnimate: prev.width !== 0,
      }));
    }
  }, [effectiveActiveIndex]);

  useEffect(() => {
    if (key === "module" && items && items.length > 0) {
      if (tabParam) {
        const matchedItem = items.find(
          (item) => item.label.toLowerCase() === tabParam.toLowerCase()
        );
        if (matchedItem) {
          localStorage.setItem("selectedModule", matchedItem.label);
          if (matchedItem.props && matchedItem.props.moduleId) {
            localStorage.setItem("selectedModuleId", String(matchedItem.props.moduleId));
          }
        }
      } else {
        const savedModule = localStorage.getItem("selectedModule");
        let matchedIndex = -1;
        if (savedModule) {
          matchedIndex = items.findIndex(
            (item) => item.label.toLowerCase() === savedModule.toLowerCase()
          );
        }
        const finalIndex = matchedIndex !== -1 ? matchedIndex : 0;
        const targetItem = items[finalIndex];

        const currentParams = new URLSearchParams(search);
        currentParams.set(key, targetItem.label);
        navigate(`${pathname}?${currentParams.toString()}`, { replace: true, state });
      }
    }
  }, [key, tabParam, items, pathname, search, navigate, state]);

  const { component: Component, props = {} } =
    items[effectiveActiveIndex] || {};

  const handleTabClick = (label: string, index: number) => {
    if (typeof onTabChange === "function") {
      onTabChange(index);
    } else {
      const currentParams = new URLSearchParams(search);
      currentParams.set(key, label);
      navigate(`${pathname}?${currentParams.toString()}`, { state });
    }
  };

  return (
    <>
      <S.StyleTabButtonContainer fitContent={fitContent}>
        <S.SlidingBackground
          $width={sliderDimensions.width}
          $left={sliderDimensions.left}
          $shouldAnimate={sliderDimensions.shouldAnimate}
        />

        {items?.map((item, index) => (
          <S.StyleTabButton
            key={index}
            ref={(el) => (tabRefs.current[index] = el)}
            $active={effectiveActiveIndex === index}
            onClick={() => handleTabClick(item.label, index)}
            type="button"
          >
            {item?.label}
          </S.StyleTabButton>
        ))}
      </S.StyleTabButtonContainer>

      <S.StyleChildrenWrapper>
        {Component ? <Component {...props} /> : null}
      </S.StyleChildrenWrapper>
    </>
  );
};

export default Tab;
